import React, { useState, useEffect, useCallback } from 'react';
import { useYearRange } from '../constants/yearRange';
import { supabase } from '../supabase';
import { pageAll } from '../utils/pageAll';
import { stateName } from '../constants/usStates';
import { formatLongDate, embedsCalendarYear } from '../constants/refFields';
import { isBppExempt } from '../utils/assessorExempt';
import { clickUnlessSelecting } from '../utils/clickGuard';
import AddAssessorModal from '../components/AddAssessorModal';
import ScrollX from '../components/ScrollX';
import AddCollectorModal from '../components/AddCollectorModal';
import ImportTemplateModal from '../components/ImportTemplateModalTwoTrack';

// Paginate a versioned reference table's full name-ordered list. assessors/collectors are per-year
// versioned (~1816 active collector rows), so a single un-ranged response caps at 1000 and silently
// drops everything after ~"M" alphabetically. Loop .range() past the cap.
async function loadAllByName(table) {
  const out = [];
  for (let from = 0; ; from += 1000) {
    const { data } = await supabase.from(table).select('*').order('name').range(from, from + 999);
    out.push(...(data || []));
    if (!data || data.length < 1000) break;
  }
  return out;
}

// Assessors & Collectors — Phase A reference-data admin.
// Sub-step (b): list + state/year scoping + assessor drill + collector drill (structure).
// Add/Edit/Retire modals are sub-step (c); link/unlink + installment editing are sub-step (d) —
// the associated-collectors and installments lists render READ-ONLY here so the drill chain works.
export default function AssessorsCollectorsPage({ darkMode }) {
  const isDark = darkMode;
  const c = isDark ? {
    card: 'rgba(47,56,84,0.6)', border: '#374060',
    text: '#e8eaf2', muted: 'var(--slate)', sub: '#a8b0cc',
    input: 'var(--ink)', inputBorder: '#434C68',
    th: 'rgba(26,34,56,0.5)', rowHover: 'rgba(255,255,255,0.04)',
  } : {
    card: '#ffffff', border: 'var(--line)',
    text: 'var(--ink)', muted: 'var(--slate)', sub: '#4a5068',
    input: '#ffffff', inputBorder: '#c8ccd8',
    th: 'var(--line-soft)', rowHover: 'rgba(0,0,0,0.02)',
  };

  const s = {
    card: { background: c.card, border: `1px solid ${c.border}`, borderRadius: '12px', overflow: 'hidden', marginBottom: '16px', boxShadow: isDark ? 'none' : '0 1px 4px rgba(0,0,0,0.06)' },
    cardH: { padding: '12px 18px', borderBottom: `1px solid ${c.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    cardT: { fontSize: '14px', fontWeight: '700', color: c.text },
    th: { padding: '9px 14px', textAlign: 'left', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: c.muted, background: c.th, borderBottom: `1px solid ${c.border}`, whiteSpace: 'nowrap' },
    td: { padding: '9px 14px', fontSize: '13px', fontWeight: '500', color: c.text, borderBottom: `1px solid ${isDark ? 'rgba(67,76,104,0.25)' : '#eef0f6'}`, whiteSpace: 'nowrap' },
    badge: (col) => ({ background: `rgba(${col},0.12)`, color: `rgb(${col})`, border: `1px solid rgba(${col},0.25)`, borderRadius: '20px', padding: '2px 9px', fontSize: '11px', fontWeight: '600' }),
    input: { padding: '8px 12px', background: c.input, border: `1px solid ${c.inputBorder}`, borderRadius: '7px', color: c.text, fontSize: '13px', outline: 'none', fontFamily: "'Public Sans',system-ui,sans-serif" },
    back: { cursor: 'pointer', color: 'var(--red)', fontSize: '13px', fontWeight: '600' },
    btn: { padding: '8px 14px', background: 'linear-gradient(135deg,#B40000,#9C0000)', border: 'none', borderRadius: '7px', color: 'white', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif", whiteSpace: 'nowrap' },
    btnGhost: { padding: '6px 12px', background: 'transparent', border: `1px solid ${c.border}`, borderRadius: '7px', color: c.muted, fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif", whiteSpace: 'nowrap' },
    fieldLabel: { fontSize: '11px', fontWeight: '700', color: c.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' },
    fieldVal: { fontSize: '13px', color: c.text, wordBreak: 'break-word' },
    notice: { margin: '10px 18px 0', padding: '7px 12px', fontSize: '12px', fontWeight: '600', color: '#b45309', background: 'rgba(217,119,6,0.10)', border: '1px solid rgba(217,119,6,0.30)', borderRadius: '8px' },
  };

  // ── State ──────────────────────────────────────────────────────
  const [assessors, setAssessors] = useState([]);
  const [collectors, setCollectors] = useState([]); // full list — source for the "link existing" picker (d)
  const [loading, setLoading] = useState(true);
  const [collectorsByAssessor, setCollectorsByAssessor] = useState({}); // assessor_id -> [collector, ...] (inline nest + "0 collectors" flag)
  const [expandedAssessors, setExpandedAssessors] = useState(() => new Set()); // assessor ids expanded in the list
  const [selectedState, setSelectedState] = useState(''); // resolved from the data once it loads
  const [valueYear, setValueYear] = useState('2026'); // fixed year list, default current value year
  const [yearNotice, setYearNotice] = useState(''); // inline notice when a state has no data for a picked year
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState(''); // live filter over assessors + their nested collectors (within the selected state)
  const [drill, setDrill] = useState(null);              // selected assessor
  const [assocCollectors, setAssocCollectors] = useState([]); // collectors linked to drill (read-only here)
  const [drillCollector, setDrillCollector] = useState(null); // selected collector
  const [collectorAssessor, setCollectorAssessor] = useState(null); // assessor the collector was drilled in from (autofill context)
  const [installments, setInstallments] = useState([]);  // installments for drillCollector (read-only display)
  const [instCountByCollector, setInstCountByCollector] = useState({}); // collector_id -> count (nested-view column)
  const [instAdjByCollector, setInstAdjByCollector] = useState({}); // collector_id -> { RE:[types], PP:[types] } distinct adjustment types
  const [addAssessorOpen, setAddAssessorOpen] = useState(false);
  const [editAssessor, setEditAssessor] = useState(null);
  const [addCollectorOpen, setAddCollectorOpen] = useState(false); // add = create + auto-link to drill assessor
  const [editCollector, setEditCollector] = useState(null);
  // Backfill: the nearest-year row to seed a NEW version for the selected (empty) year from.
  const [seedAssessor, setSeedAssessor] = useState(null);
  const [seedCollector, setSeedCollector] = useState(null);
  const [linkPick, setLinkPick] = useState('');     // collector id selected in the "link existing" picker


  // ── Loads ──────────────────────────────────────────────────────
  // The junction, grouped client-side into { assessor_id: [collector, ...] }. Drives BOTH the inline nest AND the
  // "0 collectors" flag (count = array length) — no separate count query, no N+1 on expand.
  // ⛔ PAGED (Philip, 2026-10-11). This was ONE select, under a note that "AL is ~67 counties (far under)". The
  // junction is now well past PostgREST's silent 1000-row cap (GA alone adds 309 links, MA 335), so the newest links
  // — every state just imported — fell off the end and their assessors read "0 collectors" while the links existed.
  // The collectors were also fetched with .in(entity_id, <every linked id>), a URL that grows with the junction;
  // now the selected year's collectors are paged instead. Ordered, so pages don't overlap or skip.
  // Versioning: assessor_collectors links ENTITY↔ENTITY (assessor_id/collector_id = entity_id, FK → *_entities),
  // so we can't embed collectors(*) anymore. Fetch the junction, then resolve each collector's version for the
  // selected year by (entity_id, value_year). Nest is keyed by assessor ENTITY_id; installments by collector entity_id + tax_year.
  const refreshCollectorMap = useCallback(async () => {
    const yr = Number(valueYear);
    let links, insts, cols;
    try {
      [links, insts, cols] = await Promise.all([
      pageAll(() => supabase.from('assessor_collectors').select('assessor_id, collector_id').order('assessor_id').order('collector_id')),
      pageAll(() => supabase.from('collector_installments').select('id, collector_id, kind, adjustment_type, tax_year').eq('tax_year', yr).order('id')),
      pageAll(() => supabase.from('collectors').select('*').eq('value_year', yr).order('id')),
      ]);
    } catch (e) { console.error('[assessors] collector map load failed:', e); return; }   // keep what is on screen; never a false "0 collectors"
    const colByEntity = {};
    (cols || []).forEach(c => { colByEntity[c.entity_id] = c; });
    const m = {}; // assessor entity_id -> [collector version, ...]
    (links || []).forEach(l => { const c = colByEntity[l.collector_id]; if (c) (m[l.assessor_id] = m[l.assessor_id] || []).push(c); });
    Object.values(m).forEach(arr => arr.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    setCollectorsByAssessor(m);
    const counts = {}, adj = {}; // collector entity_id -> { RE, PP } count / { RE:Set, PP:Set } adj types (this year only)
    (insts || []).forEach(r => {
      if ((r.kind !== 'RE' && r.kind !== 'PP') || r.tax_year !== yr) return;
      const e = counts[r.collector_id] || (counts[r.collector_id] = { RE: 0, PP: 0 }); e[r.kind] += 1;
      if (r.adjustment_type) { const a = adj[r.collector_id] || (adj[r.collector_id] = { RE: new Set(), PP: new Set() }); a[r.kind].add(r.adjustment_type); }
    });
    setInstCountByCollector(counts);
    setInstAdjByCollector(Object.fromEntries(Object.entries(adj).map(([k, v]) => [k, { RE: [...v.RE], PP: [...v.PP] }])));
  }, [valueYear]);

  const reloadAll = useCallback(() => {
    Promise.all([
      loadAllByName('assessors'),                                                            // all years (paginated) — list filters by value_year client-side
      // the selected year's versions (link picker) — paged: one select stopped at 1000 and dropped every name after it
      pageAll(() => supabase.from('collectors').select('*').eq('value_year', Number(valueYear)).order('name').order('id')),
    ]).then(([a, col]) => {
      setAssessors(a || []);
      setCollectors(col || []);
      setLoading(false);
    }, (e) => { console.error('[assessors] load failed:', e); setLoading(false); });
    refreshCollectorMap();
  }, [refreshCollectorMap, valueYear]);
  useEffect(() => { reloadAll(); }, [reloadAll]);
  const [importOpen, setImportOpen] = useState(false);

  const toggleAssessorExpand = (id) =>
    setExpandedAssessors(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const reloadAssoc = useCallback(async (assessorEntityId) => {
    const { data: links } = await supabase.from('assessor_collectors')
      .select('collector_id').eq('assessor_id', assessorEntityId);
    const ids = [...new Set((links || []).map(l => l.collector_id))];
    const { data } = ids.length
      ? await supabase.from('collectors').select('*').in('entity_id', ids).eq('value_year', Number(valueYear))
      : { data: [] };
    setAssocCollectors((data || []).sort((x, y) => (x.name || '').localeCompare(y.name || '')));
  }, [valueYear]);

  const loadDrill = useCallback((assessor) => {
    setDrill(assessor);
    setDrillCollector(null);
    setInstallments([]);
    setAssocCollectors([]);
    setYearNotice('');
    reloadAssoc(assessor.entity_id);   // link is entity↔entity
  }, [reloadAssoc]);

  // Installments hang off the collector ENTITY + tax_year (collector_id = entity_id after the re-point).
  // yr is passed explicitly by callers that just changed the year: valueYear is stale in this closure
  // until the next render, so relying on it here loads the WRONG year's installments (the #8 bug —
  // switching 2026→2025→2026 showed 2025's rows). Defaults to valueYear for callers not mid-switch.
  const loadInstallments = useCallback(async (collectorEntityId, yr = valueYear) => {
    const { data } = await supabase.from('collector_installments')
      .select('*').eq('collector_id', collectorEntityId).eq('tax_year', Number(yr))
      .order('installment_number', { ascending: true });
    setInstallments(data || []);
  }, [valueYear]);

  const loadCollector = useCallback((collector, parentAssessor) => {
    setDrillCollector(collector);
    setCollectorAssessor(parentAssessor || null); // context: which assessor's view we opened it under
    setInstallments([]);
    setYearNotice('');
    loadInstallments(collector.entity_id);
  }, [loadInstallments]);

  const refreshAssessors = useCallback(() =>
    loadAllByName('assessors').then(setAssessors), []);

  const refreshCollectors = useCallback(() =>
    loadAllByName('collectors').then(setCollectors), []);

  async function toggleAssessorActive() {
    const next = !drill.active;
    if (!window.confirm(next ? `Reactivate ${drill.name}?` : `Retire ${drill.name}? It'll be hidden unless "Show inactive" is on.`)) return;
    await supabase.from('assessors').update({ active: next }).eq('id', drill.id);
    const updated = { ...drill, active: next };
    setDrill(updated);
    setAssessors(prev => prev.map(a => a.id === drill.id ? updated : a));
  }

  async function toggleCollectorActive() {
    const next = !drillCollector.active;
    if (!window.confirm(next ? `Reactivate ${drillCollector.name}?` : `Retire ${drillCollector.name}?`)) return;
    await supabase.from('collectors').update({ active: next }).eq('id', drillCollector.id);
    const updated = { ...drillCollector, active: next };
    setDrillCollector(updated);
    setAssocCollectors(prev => prev.map(x => x.id === drillCollector.id ? updated : x));
  }

  // After an assessor add/edit: refresh list; if the drilled assessor was edited, sync it.
  const onAssessorSaved = (row) => {
    const wasAdd = addAssessorOpen;
    setAddAssessorOpen(false); setEditAssessor(null);
    refreshAssessors();
    if (drill && drill.id === row.id) setDrill(row);
    if (wasAdd) loadDrill(row); // new assessor → land on its detail; user chooses link-existing OR create-new
  };

  // Leave an assessor detail (breadcrumb back) — clean, no prompt. The amber "0 collectors" flag on
  // the list stays as a passive visual cue; navigation is never blocked.
  const leaveAssessor = () => {
    refreshCollectorMap(); // keep the list nest + "0 collectors" flags accurate on return
    setDrill(null);
  };

  // After a collector ADD (auto-link to the drilled assessor) or EDIT: link if new, then refresh.
  // A SEEDED save creates a NEW row for the selected year, so its id never matches the drilled record
  // (which still holds the seed year). The normal save handlers gate on `drill.id === row.id` and so
  // leave the detail pinned to the seed year, still rendering the "no <year> record" empty state until a
  // manual refresh. These re-point the drill at the just-created version, the same resolution the
  // year-switch performs.
  const onAssessorSeeded = (row) => {
    setSeedAssessor(null);
    refreshAssessors();
    setDrill(row);
    setYearNotice('');
  };
  const onCollectorSeeded = (row) => {
    setSeedCollector(null);
    refreshCollectors();
    refreshCollectorMap();
    if (drill) reloadAssoc(drill.id);
    setDrillCollector(row);
    loadInstallments(row.entity_id);   // entity anchor, not the year-scoped row id
    setYearNotice('');
  };

  const onCollectorSaved = async (row) => {
    const wasAdd = addCollectorOpen;
    setAddCollectorOpen(false); setEditCollector(null);
    if (wasAdd && drill) {
      await supabase.from('assessor_collectors').insert({ assessor_id: drill.entity_id, collector_id: row.entity_id });
    }
    if (drill) reloadAssoc(drill.id);
    // Installments key on the ENTITY anchor, not the year-scoped row id — those coincide only until a
    // collector gains a second year-version (which backfill creates), after which row.id finds nothing.
    if (drillCollector && drillCollector.id === row.id) { setDrillCollector(row); loadInstallments(row.entity_id); }
    refreshCollectors();
    refreshCollectorMap(); // nested-view collectors + installment counts
  };

  // ── (d) association + installments ─────────────────────────────
  const eligibleCollectors = collectors
    .filter(col => col.state === (drill ? drill.state : null) && !assocCollectors.some(a => a.entity_id === col.entity_id))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const linkCollector = async () => {
    if (!linkPick || !drill) return;
    await supabase.from('assessor_collectors').insert({ assessor_id: drill.entity_id, collector_id: linkPick });
    setLinkPick('');
    reloadAssoc(drill.id);
  };

  const unlinkCollector = async (collectorId) => {
    if (!window.confirm('Unlink this collector from the assessor? The collector record stays (it may be linked to other assessors).')) return;
    await supabase.from('assessor_collectors').delete().eq('assessor_id', drill.entity_id).eq('collector_id', collectorId);
    reloadAssoc(drill.id);
  };

  // Installments are now edited in the collector modal (clean-replace on save); the detail view
  // renders them read-only via instBox. No per-row edit machinery here.

  // ── Derived scoping ────────────────────────────────────────────
  // States that actually have records — fully data-driven, no hardcoded seed. Empty states (Alabama et al)
  // never appear; a newly imported state shows up on its own. Derived from `assessors`, which is loaded
  // for ALL years; `collectors` is year-scoped by one of its two loaders and all-years by the other, so
  // using it here would make the list flicker with whichever ran last. Every collector links to an
  // assessor in the same state, so nothing is lost.
  const availableStates = Array.from(new Set(assessors.map(a => a.state).filter(Boolean)))
    .sort((a, b) => stateName(a).localeCompare(stateName(b)));
  const { years: YEARS } = useYearRange();
  // Does this STATE have assessor records in OTHER years? Only asked when the current year's list comes
  // back empty, and it is a HEAD count (no rows fetched) — so the common case costs nothing. Distinguishes
  // "we don't cover this state" from "this year isn't backfilled yet", which the old message conflated.
  const [otherYearRows, setOtherYearRows] = useState(null);   // null = not checked / unknown
   // folder_year_config — was a fixed literal (see yearRange.js)

  // Land on a state that actually has records, and re-home if the current one stops having any (last
  // assessor retired, or an import reshapes the set). Keyed on the joined list so the effect doesn't
  // re-fire on every render — availableStates is a fresh array each time.
  const statesKey = availableStates.join(',');
  useEffect(() => {
    if (availableStates.length && !availableStates.includes(selectedState)) setSelectedState(availableStates[0]);
  }, [statesKey]);   // eslint-disable-line react-hooks/exhaustive-deps
  const visibleAssessors = assessors.filter(a =>
    a.state === selectedState &&
    a.value_year === Number(valueYear) &&
    (showInactive || a.active)
  );

  // Live search — narrows the (state-scoped) list as you type. An assessor shows if it matches OR any of its
  // collectors match; a collector-only match auto-expands the assessor and shows just the matching collectors.
  // Case-insensitive partial match across every human-identifiable field on each record.
  const q = search.trim().toLowerCase();
  const hit = (vals) => vals.some(v => v != null && v !== '' && String(v).toLowerCase().includes(q));
  const assessorFields = (a) => [a.name, a.state, stateName(a.state), a.county, a.value_year,
    a.re_assessment_date, a.re_reappraisal_year, a.re_reappraisal_cycle, a.re_fiscal_year, a.re_ratio,
    a.re_compliance_season, a.re_notice_season, a.re_appeal_season,
    a.pp_assessment_date, a.pp_reappraisal_year, a.pp_reappraisal_cycle, a.pp_fiscal_year, a.pp_ratio,
    a.pp_compliance_season, a.pp_notice_season, a.pp_appeal_season];
  const collectorFields = (col) => [col.name, col.state, stateName(col.state), col.county,
    col.re_tax_bill_season, col.re_fiscal_year, col.re_ratio,
    col.bpp_tax_bill_season, col.bpp_fiscal_year, col.bpp_ratio, col.discount_offered];
  // Fires ONLY when the year's list is empty and no search is active — the case where the old message
  // said "No assessors for Texas (2021)" whether the state was uncovered or merely unbackfilled.
  useEffect(() => {
    if (!selectedState) { setOtherYearRows(null); return; }
    if (assessors.length > 0) { setOtherYearRows(null); return; }   // list isn't empty — nothing to explain
    let alive = true;
    (async () => {
      const { count, error } = await supabase.from('assessors')
        .select('id', { count: 'exact', head: true })
        .eq('state', selectedState).neq('value_year', Number(valueYear));
      if (!alive) return;
      // A failed probe leaves it null → the message falls back to the neutral wording rather than
      // asserting either "no records anywhere" or "records exist elsewhere" on no evidence.
      if (error) { console.warn('[AssessorsCollectorsPage] other-year probe failed:', error.message); setOtherYearRows(null); return; }
      setOtherYearRows(count ?? null);
    })();
    return () => { alive = false; };
  }, [selectedState, valueYear, assessors.length]);

  const displayAssessors = visibleAssessors.map(a => {
    const nested = collectorsByAssessor[a.entity_id] || [];
    if (!q) return { a, nested, forceOpen: false };
    const aHit = hit(assessorFields(a));
    const colHits = nested.filter(col => hit(collectorFields(col)));
    if (!aHit && colHits.length === 0) return null;
    return { a, nested: aHit ? nested : colHits, forceOpen: !aHit && colHits.length > 0 };
  }).filter(Boolean);

  // Year switching (both detail screens): ALWAYS switch, even to a year with no record — that empty
  // view is the backfill entry point. The detail then renders an empty state with a "Create <year>
  // record" action instead of the previous year's values (a drill row carries the year it was loaded
  // for, so `row.value_year !== valueYear` is what tells the render it is on an empty year).
  // Blocking the switch here is what used to make backfill impossible.
  const yearsForAssessor = (a) => new Set(
    (a ? assessors.filter(x => x.name === a.name && x.state === a.state && x.county === a.county && x.value_year != null) : []).map(x => x.value_year)
  );
  const tryChangeYear = (y, identityAssessor, onSwitch) => {
    const has = yearsForAssessor(identityAssessor).has(Number(y));
    setValueYear(y);
    setYearNotice(has ? '' : `No records have been entered for ${identityAssessor?.name || 'this assessor'} in ${y} yet — use “Create ${y} record” below to start one.`);
    if (onSwitch) onSwitch(y, has);
  };

  // Collector detail year switch. The in-memory `collectors` list is scoped to the selected year, so
  // resolve the target year's version straight from the DB on the ENTITY anchor. Always switches: a
  // missing year renders the backfill empty state (drillCollector stays as the identity anchor, and
  // `row.value_year !== valueYear` is what the render keys on).
  const changeCollectorYear = async (y) => {
    setValueYear(y);
    const eid = drillCollector?.entity_id;
    if (!eid) return;
    const { data } = await supabase.from('collectors').select('*')
      .eq('entity_id', eid).eq('value_year', Number(y)).maybeSingle();
    if (data) { setDrillCollector(data); setYearNotice(''); loadInstallments(eid, y); }
    else setYearNotice(`No records have been entered for ${drillCollector.name} in ${y} yet — use “Create ${y} record” below to start one.`);
    // Keep the assessor context (name/ratio) on the same year when it has a version.
    const m = assessors.find(x => collectorAssessor && x.name === collectorAssessor.name && x.state === collectorAssessor.state
      && x.county === collectorAssessor.county && x.value_year === Number(y));
    if (m) setCollectorAssessor(m);
  };

  // Auto-dismiss the year-guardrail notice ~4s after it's set. Keyed on the message, so a new notice
  // resets the timer (cleanup clears the prior one); cleanup also runs on unmount.
  useEffect(() => {
    if (!yearNotice) return undefined;
    const t = setTimeout(() => setYearNotice(''), 4000);
    return () => clearTimeout(t);
  }, [yearNotice]);

  // Two-track LIST layout — three column-groups (General | Real Estate | Business Personal Property).
  const ASSESSOR_GROUPS = [
    { label: 'General', cols: [['state', 'State'], ['name', 'Assessor'], ['value_year', 'Value Year']] },
    { label: 'Real Estate', cols: [['re_assessment_date', 'Assessment Date'], ['re_reappraisal_year', 'Reappraisal Year'], ['re_reappraisal_cycle', 'Reappraisal Cycle'], ['re_fiscal_year', 'Fiscal Year'], ['re_ratio', 'Ratio']] },
    { label: 'Business Personal Property', cols: [['pp_assessment_date', 'Assessment Date'], ['pp_reappraisal_year', 'Reappraisal Year'], ['pp_reappraisal_cycle', 'Reappraisal Cycle'], ['pp_fiscal_year', 'Fiscal Year'], ['pp_ratio', 'Ratio']] },
  ];
  const ASSESSOR_COLS = ASSESSOR_GROUPS.flatMap(g => g.cols);

  // Backfill warning: a year-version still carrying seeded provenance, or a NOTICE SEASON naming a year other than
  // its own (a 2025 row still saying "October 2026" — a carried value that slipped through). Notice Season only
  // (Philip, 2026-10-09): the other seasons legitimately fall in the year before or after (the pickers offer
  // value year −1 … +2), so a different year there is not a sign of a carry. Returns the tooltip reason, or ''.
  const incompleteYear = (a) => {
    if (!a) return '';
    if (a.seeded_from_year != null) return `Seeded from ${a.seeded_from_year} — not yet verified for ${a.value_year}`;
    const wrongYear = ['re', 'pp'].flatMap(t => ['notice_season']
      .filter(f => embedsCalendarYear(a[`${t}_${f}`]) && !String(a[`${t}_${f}`]).includes(String(a.value_year)))
      .map(f => `${t === 're' ? 'RE' : 'BPP'} ${f.replace(/_/g, ' ')}`));
    return wrongYear.length ? `Holds another year's value: ${wrongYear.join(', ')}` : '';
  };
  const CENTER_COLS = new Set(['value_year', ...ASSESSOR_GROUPS.slice(1).flatMap(g => g.cols.map(([k]) => k))]);

  // Display-only: a bare decimal ratio (e.g. "0.4", "0.40", ".4", 0.4) → "40%". Already-formatted
  // text (e.g. "Class 1: 30%, …") is left UNCHANGED. Stored value is untouched (re-import safe).
  const formatRatio = (v) => {
    if (v == null || String(v).trim() === '') return '—';
    const sv = String(v).trim();
    return /^\d*\.?\d+$/.test(sv) ? `${parseFloat((parseFloat(sv) * 100).toFixed(4))}%` : sv;
  };

  // An assessor's BPP track is exempt when the sheet's BPP columns read "Exempt". Assessors carry no
  // pp_exempt flag, so detect from the pp_* TEXT columns (the importer wrote the literal "Exempt" there;
  // the numeric pp_reappraisal_year/pp_ratio became 0 via numOrNull). Drives a single "Exempt" across
  // the whole BPP group so the numeric cols never show 0/0%.
  const bppExempt = (a) => isBppExempt(a);

  // Detail-view track section: a red sub-heading + a 3-col field grid (RE and BPP each render one).
  const trackHead = { fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--red)', margin: '2px 0 10px', paddingBottom: '6px', borderBottom: `1px solid ${c.border}` };
  const grid3 = { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px', marginBottom: '18px' };
  const exemptVal = { fontWeight: '700', color: c.text, padding: '2px 0' };

  // Display-only: join 5 address parts into one line, skipping blanks cleanly. Shared by the assessor
  // AND collector detail views. e.g. "P.O. Box 1119, Talladega, AL 35161". Storage unchanged.
  const formatAddress = (a1, a2, city, st, zip) => {
    const parts = [a1, a2, city].map(x => (x || '').trim()).filter(Boolean);
    const stateZip = [st, zip].map(x => (x || '').trim()).filter(Boolean).join(' ');
    if (stateZip) parts.push(stateZip);
    return parts.join(', ');
  };

  // Per-track installment count + distinct adjustment-type summary for the nested collector view.
  const instCount = (collectorId, kind) => { const e = instCountByCollector[collectorId]; return e ? e[kind] : 0; };
  const adjSummary = (collectorId, kind) => { const e = instAdjByCollector[collectorId]; const arr = e ? e[kind] : null; return arr && arr.length ? arr.join(', ') : '—'; };

  // Read-only installment box (one per kind) for the collector detail view.
  const instBox = (title, kind) => {
    const rows = installments.filter(it => it.kind === kind)
      .sort((a, b) => (a.installment_number || 0) - (b.installment_number || 0));
    return (
      <div style={{ flex: '1 1 280px', border: `1px solid ${c.border}`, borderRadius: '8px', padding: '12px 14px' }}>
        <div style={{ fontSize: '12px', fontWeight: '700', color: c.text, marginBottom: '8px' }}>{title}</div>
        {rows.length === 0
          ? <div style={{ fontSize: '12px', color: c.muted, fontStyle: 'italic' }}>None.</div>
          : rows.map(it => (
            <div key={it.id} style={{ marginBottom: '8px' }}>
              {/* Main line — # · Split % · Due Date */}
              <div style={{ fontSize: '13px', color: c.text }}>
                <strong>Installment {it.installment_number ?? '—'}</strong>
                <span style={{ color: c.muted }}>{'  ·  '}{it.split_pct != null ? it.split_pct + '%' : '—'}{'  ·  Due '}{formatLongDate(it.due_date) || '—'}</span>
              </div>
              {/* Adjustment sub-line — only when present */}
              {it.adjustment_type && (
                <div style={{ fontSize: '12px', color: c.muted, marginLeft: '14px', marginTop: '2px' }}>
                  ↳ {it.adjustment_type}{it.adjustment_pct != null ? ' ' + it.adjustment_pct + '%' : ''}
                </div>
              )}
            </div>
          ))}
      </div>
    );
  };

  const field = (label, value, isLink) => (
    <div key={label}>
      <div style={s.fieldLabel}>{label}</div>
      <div style={s.fieldVal}>
        {isLink && value
          ? <a href={value} target="_blank" rel="noreferrer" style={{ color: 'var(--red)' }}>{value}</a>
          : (value == null || value === '' ? '—' : String(value))}
      </div>
    </div>
  );

  if (loading) return <div style={{ padding: '24px', color: c.muted, fontSize: '13px' }}>Loading…</div>;

  // ── COLLECTOR DETAIL ───────────────────────────────────────────
  if (drillCollector) {
    // Same rule as the assessor detail: a drill row carries the year it was loaded for, so a mismatch
    // against the selected year means this entity has NO version for that year.
    const collectorYearMissing = Number(drillCollector.value_year) !== Number(valueYear);
    return (
      <div style={{ width: '100%' }}>
        <div style={{ marginBottom: '14px', fontSize: '13px' }}>
          <span style={s.back} onClick={() => setDrillCollector(null)}>← {drill ? drill.name : 'Assessors'}</span>
          <span style={{ color: c.muted }}> / {drillCollector.name}</span>
        </div>
        <div style={s.card}>
          <div style={s.cardH}><div style={s.cardT}>{drillCollector.name}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {!drillCollector.active && <span style={s.badge('248,113,113')}>Inactive</span>}
              <select style={{ ...s.btnGhost, padding: '6px 10px' }} value={valueYear} title="Value year being viewed" onChange={e => changeCollectorYear(e.target.value)}>
                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              {!collectorYearMissing && (
                <button style={s.btnGhost} onClick={() => setEditCollector(drillCollector)}>Edit</button>
              )}
              <button style={s.btnGhost} onClick={toggleCollectorActive}>{drillCollector.active ? 'Retire' : 'Unretire'}</button>
            </div></div>
          {yearNotice && <div style={s.notice}>{yearNotice}</div>}
          {collectorYearMissing ? (
            <div style={{ padding: '28px 18px', textAlign: 'center' }}>
              <div style={{ fontSize: '14px', fontWeight: '700', color: c.text, marginBottom: '6px' }}>
                No {valueYear} record for {drillCollector.name}
              </div>
              <div style={{ fontSize: '12px', color: c.muted, maxWidth: '560px', margin: '0 auto 16px', lineHeight: 1.5 }}>
                Creating one copies every field and installment from {drillCollector.value_year} except
                {' '}<strong>Tax Bill Season</strong> and the installment <strong>due dates</strong>, whose stored values have
                the year baked in. Those start blank and must be entered for {valueYear}; the record stays flagged until
                they are.
              </div>
              <button style={s.btn} title={`Seed a ${valueYear} version from ${drillCollector.value_year}`}
                onClick={() => setSeedCollector(drillCollector)}>
                Create {valueYear} record
              </button>
            </div>
          ) : (
          <div style={{ padding: '16px 18px' }}>
            <div style={grid3}>
              {field('State', stateName(drillCollector.state))}
              {field('Discount Offered', drillCollector.discount_offered || 'No')}
              {field('Non-ad valorem billed on', drillCollector.nav_billed_on === 'installment_1' ? 'Installment 1' : 'Split evenly')}
              {field('URL', drillCollector.url, true)}
              {field('Mailing Address', formatAddress(drillCollector.mailing_address_1, drillCollector.mailing_address_2, drillCollector.mailing_city, drillCollector.mailing_state, drillCollector.mailing_zip))}
            </div>
            <div style={trackHead}>Real Estate</div>
            <div style={grid3}>
              {field('Tax Bill Season', drillCollector.re_tax_bill_season)}
              {field('Fiscal Year', drillCollector.re_fiscal_year)}
              {field('Assessment Ratio', formatRatio(drillCollector.re_ratio))}
            </div>
            <div style={trackHead}>Business Personal Property</div>
            {drillCollector.pp_exempt
              ? <div style={exemptVal}>Exempt</div>
              : <div style={{ ...grid3, marginBottom: 0 }}>
                  {field('Tax Bill Season', drillCollector.bpp_tax_bill_season)}
                  {field('Fiscal Year', drillCollector.bpp_fiscal_year)}
                  {field('Assessment Ratio', formatRatio(drillCollector.bpp_ratio))}
                </div>}
          </div>
          )}
        </div>
        {!collectorYearMissing && (
          <div style={s.card}>
            <div style={s.cardH}><div style={s.cardT}>Installments</div></div>
            <div style={{ padding: '16px 18px', display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
              {instBox('Real Estate', 'RE')}
              {instBox('Personal Property', 'PP')}
            </div>
          </div>
        )}
        {editCollector && <AddCollectorModal isDark={isDark} collector={editCollector} assessor={collectorAssessor} defaultState={selectedState} valueYear={valueYear} onClose={() => setEditCollector(null)} onSuccess={onCollectorSaved} />}
        {seedCollector && <AddCollectorModal isDark={isDark} collector={null} seed={seedCollector} assessor={collectorAssessor} defaultState={selectedState} valueYear={valueYear} onClose={() => setSeedCollector(null)} onSuccess={onCollectorSeeded} />}
      </div>
    );
  }

  // ── ASSESSOR DETAIL ────────────────────────────────────────────
  if (drill) {
    // The drill row carries the year it was loaded for. When that differs from the selected year, this
    // entity has NO version for the selected year — render the backfill empty state, never drill's values.
    const yearMissing = Number(drill.value_year) !== Number(valueYear);
    return (
      <div style={{ width: '100%' }}>
        <div style={{ marginBottom: '14px', fontSize: '13px' }}>
          <span style={s.back} onClick={leaveAssessor}>← Assessors &amp; Collectors</span>
          <span style={{ color: c.muted }}> / {drill.name}</span>
        </div>
        <div style={s.card}>
          <div style={s.cardH}><div style={s.cardT}>{drill.name}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {!drill.active && <span style={s.badge('248,113,113')}>Inactive</span>}
              <select style={{ ...s.btnGhost, padding: '6px 10px' }} value={valueYear} title="Value year being viewed" onChange={e => tryChangeYear(e.target.value, drill, (y) => {
                const m = assessors.find(x => x.name === drill.name && x.state === drill.state && x.county === drill.county && x.value_year === Number(y));
                if (m) setDrill(m); // that year's record; otherwise keep drill as the identity anchor
              })}>
                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              {!yearMissing && (
                <button style={s.btnGhost} onClick={() => setEditAssessor(drill)}>Edit</button>
              )}
              <button style={s.btnGhost} onClick={toggleAssessorActive}>{drill.active ? 'Retire' : 'Unretire'}</button>
            </div></div>
          {yearNotice && <div style={s.notice}>{yearNotice}</div>}
          {yearMissing ? (
            <div style={{ padding: '28px 18px', textAlign: 'center' }}>
              <div style={{ fontSize: '14px', fontWeight: '700', color: c.text, marginBottom: '6px' }}>
                No {valueYear} record for {drill.name}
              </div>
              <div style={{ fontSize: '12px', color: c.muted, maxWidth: '520px', margin: '0 auto 16px', lineHeight: 1.5 }}>
                Creating one copies every field from {drill.value_year} except <strong>Notice Season</strong>, whose stored
                value has the year baked in. That starts blank and must be entered for {valueYear}; the record stays
                flagged until it is.
              </div>
              <button style={s.btn} title={`Seed a ${valueYear} version from ${drill.value_year}`}
                onClick={() => setSeedAssessor(drill)}>
                Create {valueYear} record
              </button>
            </div>
          ) : (
          <div style={{ padding: '16px 18px' }}>
            <div style={grid3}>
              {field('State', stateName(drill.state))}
              {field('Value Year', drill.value_year)}
              {field('URL', drill.url, true)}
              {field('Mailing Address', formatAddress(drill.mailing_address_1, drill.mailing_address_2, drill.mailing_city, drill.mailing_state, drill.mailing_zip))}
            </div>
            <div style={trackHead}>Real Estate</div>
            <div style={grid3}>
              {field('Assessment Date', drill.re_assessment_date)}
              {field('Reappraisal Year', drill.re_reappraisal_year)}
              {field('Reappraisal Cycle', drill.re_reappraisal_cycle)}
              {field('Fiscal Year', drill.re_fiscal_year)}
              {field('Assessment Ratio', formatRatio(drill.re_ratio))}
              {field('Compliance Season', drill.re_compliance_season)}
              {field('Extensions', drill.re_extensions)}
              {field('Notice Season', drill.re_notice_season)}
              {field('Appeal Season', drill.re_appeal_season)}
            </div>
            <div style={trackHead}>Business Personal Property</div>
            {bppExempt(drill)
              ? <div style={exemptVal}>Exempt</div>
              : <div style={{ ...grid3, marginBottom: 0 }}>
                  {field('Assessment Date', drill.pp_assessment_date)}
                  {field('Reappraisal Year', drill.pp_reappraisal_year)}
                  {field('Reappraisal Cycle', drill.pp_reappraisal_cycle)}
                  {field('Fiscal Year', drill.pp_fiscal_year)}
                  {field('Assessment Ratio', formatRatio(drill.pp_ratio))}
                  {field('Compliance Season', drill.pp_compliance_season)}
                  {field('Extensions', drill.pp_extensions)}
                  {field('Notice Season', drill.pp_notice_season)}
                  {field('Appeal Season', drill.pp_appeal_season)}
                </div>}
          </div>
          )}
        </div>
        <div style={s.card}>
          <div style={s.cardH}><div style={s.cardT}>Associated Collectors</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={s.badge('52,211,153')}>{assocCollectors.length}</span>
              <button style={s.btn} onClick={() => setAddCollectorOpen(true)}>+ Create New Collector</button>
            </div></div>
          <div style={{ padding: '10px 16px', borderBottom: `1px solid ${c.border}`, display: 'flex', gap: '8px', alignItems: 'center' }}>
            <select style={{ ...s.input, maxWidth: '320px' }} value={linkPick} onChange={e => setLinkPick(e.target.value)}>
              <option value="">Link existing collector…</option>
              {eligibleCollectors.map(col => <option key={col.id} value={col.entity_id}>{col.name}{col.county ? ` — ${col.county}` : ''}</option>)}
            </select>
            <button onClick={linkCollector} disabled={!linkPick}
              style={{ ...s.btnGhost, opacity: linkPick ? 1 : 0.5, cursor: linkPick ? 'pointer' : 'not-allowed' }}>Link</button>
            {eligibleCollectors.length === 0 && <span style={{ fontSize: '12px', color: c.muted }}>No other {stateName(drill.state)} collectors to link.</span>}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={s.th}>Name</th><th style={{ ...s.th, textAlign: 'center' }}>Fiscal Year</th><th style={{ ...s.th, textAlign: 'center' }}>Discount</th><th style={s.th}></th></tr></thead>
            <tbody>
              {assocCollectors.length === 0
                ? <tr><td colSpan="4" style={{ ...s.td, textAlign: 'center', color: c.muted }}>No collectors associated yet.</td></tr>
                : assocCollectors.map(col => (
                  <tr key={col.id} onClick={clickUnlessSelecting(() => loadCollector(col, drill))} style={{ cursor: 'pointer', opacity: col.active ? 1 : 0.6 }}
                    onMouseEnter={e => e.currentTarget.style.background = c.rowHover}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ ...s.td, fontWeight: '600', color: 'var(--red)' }}>{col.name}</td>
                    <td style={{ ...s.td, textAlign: 'center' }}>{col.re_fiscal_year || '—'}</td>
                    <td style={{ ...s.td, textAlign: 'center' }}>{col.discount_offered || 'No'}</td>
                    <td style={s.td}><button style={s.btnGhost} onClick={e => { e.stopPropagation(); unlinkCollector(col.entity_id); }}>Unlink</button></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {editAssessor && <AddAssessorModal isDark={isDark} assessor={editAssessor} defaultState={selectedState} valueYear={valueYear} onClose={() => setEditAssessor(null)} onSuccess={onAssessorSaved} />}
        {seedAssessor && <AddAssessorModal isDark={isDark} assessor={null} seed={seedAssessor} defaultState={selectedState} valueYear={valueYear} onClose={() => setSeedAssessor(null)} onSuccess={onAssessorSeeded} />}
        {addCollectorOpen && <AddCollectorModal isDark={isDark} collector={null} assessor={drill} defaultState={drill.state || selectedState} valueYear={valueYear} onClose={() => setAddCollectorOpen(false)} onSuccess={onCollectorSaved} />}
      </div>
    );
  }

  // ── ASSESSOR LIST (scoped) ─────────────────────────────────────
  return (
    <div style={{ width: '100%' }}>
      <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '24px', fontWeight: '700', color: c.text, marginBottom: '6px' }}>Assessors &amp; Collectors</div>
      <div style={{ fontSize: '13px', color: c.muted, marginBottom: '18px' }}>Reference tables for assessor &amp; collector data, scoped by state.</div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative' }}>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search assessors & collectors…" style={{ ...s.input, width: '280px', paddingRight: search ? '26px' : undefined }} />
          {search && <button type="button" onClick={() => setSearch('')} title="Clear search" style={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: c.muted, cursor: 'pointer', fontSize: '13px', lineHeight: 1, padding: '2px 4px' }}>✕</button>}
        </div>
        <select style={s.input} value={selectedState} onChange={e => { setSelectedState(e.target.value); setYearNotice(''); }}
          disabled={availableStates.length === 0}>
          {availableStates.length === 0
            ? <option value="">No states with records yet</option>
            : availableStates.map(code => <option key={code} value={code}>{stateName(code)}</option>)}
        </select>
        <select style={s.input} value={valueYear} onChange={e => setValueYear(e.target.value)}>
          {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: c.muted, cursor: 'pointer' }}>
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} style={{ cursor: 'pointer' }} />
          Show inactive
        </label>
        <button style={{ ...s.btnGhost, marginLeft: 'auto' }} onClick={() => setImportOpen(true)}>⬆ Import Template</button>
        {importOpen && <ImportTemplateModal isDark={isDark} onClose={() => setImportOpen(false)} onImported={reloadAll} />}
        <button style={{ ...s.btn, marginLeft: 'auto' }} onClick={() => setAddAssessorOpen(true)}>+ Add Assessor</button>
      </div>

      <div style={s.card}>
        <div style={s.cardH}>
          <div style={s.cardT}>{stateName(selectedState)}</div>
          <span style={{ fontSize: '12px', color: c.muted }}>{displayAssessors.length} shown{q ? ` for “${search.trim()}”` : ''}</span>
        </div>
        {/* bounded viewport — rows scroll inside (both axes) so the sticky <thead> can stick to top:0; ScrollX adds the
            top scrollbar (the shared one — this page had its own before 2026-10-27) */}
        <ScrollX style={{ overflow: 'auto', maxHeight: '70vh' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...s.th, width: '36px', position: 'sticky', top: 0, zIndex: 3 }} rowSpan={2}></th>
                {ASSESSOR_GROUPS.map((g, gi) => <th key={g.label} colSpan={g.cols.length} style={{ ...s.th, textAlign: 'center', position: 'sticky', top: 0, zIndex: 3, borderLeft: gi > 0 ? `2px solid ${c.border}` : undefined }}>{g.label}</th>)}
              </tr>
              <tr>
                {ASSESSOR_GROUPS.flatMap((g, gi) => g.cols.map(([key, label], ci) => <th key={key} style={{ ...(CENTER_COLS.has(key) ? { ...s.th, textAlign: 'center' } : s.th), position: 'sticky', top: '31px', zIndex: 2, borderLeft: (gi > 0 && ci === 0) ? `2px solid ${c.border}` : undefined }}>{label}</th>))}
              </tr>
            </thead>
            <tbody>
              {displayAssessors.length === 0
                ? <tr><td colSpan={ASSESSOR_COLS.length + 1} style={{ ...s.td, textAlign: 'center', color: c.muted }}>{q ? `No matches for “${search.trim()}” in ${stateName(selectedState)} (${valueYear}).`
                    : otherYearRows == null ? `No assessors for ${stateName(selectedState)} (${valueYear}).`
                    : otherYearRows > 0
                      /* ⛔ "No assessors" is a LIE when the office exists and only this YEAR is unfilled — the
                         list is scoped to one value_year, so an unbackfilled year renders identically to a
                         state we do not cover. Say which it is, and name the way through: switch to a year
                         that has records, open the assessor, switch back, and its drill offers "Create
                         <year> record" (that seed flow already exists, and it blanks the year-baked fields). */
                      ? `No ${valueYear} records for ${stateName(selectedState)} yet — but ${otherYearRows} record(s) exist in other years. Switch to a year that has them, open an assessor, then switch back to ${valueYear} to seed its version.`
                      : `No assessors for ${stateName(selectedState)} in any year.`}</td></tr>
                : displayAssessors.map(({ a, nested, forceOpen }) => {
                  const isOpen = expandedAssessors.has(a.id) || forceOpen;
                  return (
                  <React.Fragment key={a.id}>
                  <tr onClick={clickUnlessSelecting(() => loadDrill(a))} style={{ cursor: 'pointer', opacity: a.active ? 1 : 0.6 }}
                    onMouseEnter={e => e.currentTarget.style.background = c.rowHover}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ ...s.td, textAlign: 'center', cursor: 'pointer' }} onClick={e => { e.stopPropagation(); toggleAssessorExpand(a.id); }}>
                      <span style={{ color: 'var(--red)', display: 'inline-block', fontSize: '20px', fontWeight: '700', lineHeight: 1, transition: 'transform .15s', transform: isOpen ? 'rotate(90deg)' : 'none' }}>›</span>
                    </td>
                    {ASSESSOR_GROUPS.flatMap((g, gi) => {
                      // Exempt BPP track → one "Exempt" spanning the whole group (never 0/0% on the numeric cols).
                      if (g.label === 'Business Personal Property' && bppExempt(a)) {
                        return [<td key="bpp-exempt" colSpan={g.cols.length} style={{ ...s.td, textAlign: 'center', fontWeight: '700', color: c.text, borderLeft: `2px solid ${c.border}` }}>Exempt</td>];
                      }
                      return g.cols.map(([key], ci) => (
                        <td key={key} style={{ ...(key === 'name' ? { ...s.td, fontWeight: '600', color: 'var(--red)' } : s.td), ...(CENTER_COLS.has(key) ? { textAlign: 'center' } : {}), ...((gi > 0 && ci === 0) ? { borderLeft: `2px solid ${c.border}` } : {}) }}>
                          {key === 'name'
                            ? <>{a.name}{incompleteYear(a) && <span title={incompleteYear(a)} style={{ marginLeft: '8px', fontSize: '10px', fontWeight: '700', color: '#a16207', background: 'rgba(234,179,8,0.15)', border: '1px solid rgba(234,179,8,0.5)', borderRadius: '10px', padding: '1px 7px', whiteSpace: 'nowrap' }}>⚠ needs {valueYear} data</span>}{nested.length === 0 && <span style={{ marginLeft: '8px', fontSize: '10px', fontWeight: '700', color: '#b45309', background: 'rgba(217,119,6,0.12)', border: '1px solid rgba(217,119,6,0.35)', borderRadius: '10px', padding: '1px 7px', whiteSpace: 'nowrap' }}>0 collectors</span>}</>
                            : (key === 're_ratio' || key === 'pp_ratio')
                            ? formatRatio(a[key])
                            : (a[key] == null || a[key] === '' ? '—' : String(a[key]))}
                        </td>
                      ));
                    })}
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={ASSESSOR_COLS.length + 1} style={{ padding: 0, background: isDark ? 'rgba(255,255,255,0.03)' : '#f4f5fa', borderBottom: `1px solid ${c.border}` }}>
                        <div style={{ margin: '6px 0 6px 40px', borderLeft: '3px solid var(--red)', paddingLeft: '14px' }}>
                        {nested.length === 0
                          ? <div style={{ padding: '6px 4px', fontSize: '12px', color: c.muted, fontStyle: 'italic' }}>No collectors associated yet.</div>
                          : (
                            <table style={{ width: 'auto', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr>
                                  <th colSpan={3} style={{ ...s.th, padding: '7px 20px', textAlign: 'center' }}>General</th>
                                  <th colSpan={5} style={{ ...s.th, padding: '7px 20px', textAlign: 'center', borderLeft: `2px solid ${c.border}` }}>Real Estate</th>
                                  <th colSpan={5} style={{ ...s.th, padding: '7px 20px', textAlign: 'center', borderLeft: `2px solid ${c.border}` }}>Business Personal Property</th>
                                </tr>
                                <tr>
                                  <th style={{ ...s.th, padding: '9px 20px' }}>Collector</th><th style={{ ...s.th, padding: '9px 20px' }}>State</th><th style={{ ...s.th, padding: '9px 20px' }}>Discount</th>
                                  <th style={{ ...s.th, padding: '9px 20px', borderLeft: `2px solid ${c.border}` }}>Tax Bill Season</th><th style={{ ...s.th, padding: '9px 20px' }}>Fiscal Year</th><th style={{ ...s.th, padding: '9px 20px', textAlign: 'center' }}>Ratio</th><th style={{ ...s.th, padding: '9px 20px', textAlign: 'center' }}>Inst.</th><th style={{ ...s.th, padding: '9px 20px' }}>Adj. Types</th>
                                  <th style={{ ...s.th, padding: '9px 20px', borderLeft: `2px solid ${c.border}` }}>Tax Bill Season</th><th style={{ ...s.th, padding: '9px 20px' }}>Fiscal Year</th><th style={{ ...s.th, padding: '9px 20px', textAlign: 'center' }}>Ratio</th><th style={{ ...s.th, padding: '9px 20px', textAlign: 'center' }}>Inst.</th><th style={{ ...s.th, padding: '9px 20px' }}>Adj. Types</th>
                                </tr>
                              </thead>
                              <tbody>
                                {nested.map(col => (
                                  <tr key={col.id} onClick={clickUnlessSelecting(() => loadCollector(col, a))} style={{ cursor: 'pointer', opacity: col.active ? 1 : 0.6 }}
                                    onMouseEnter={e => e.currentTarget.style.background = c.rowHover}
                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                    <td style={{ ...s.td, padding: '9px 20px', fontWeight: '600', color: 'var(--red)' }}>{col.name}</td>
                                    <td style={{ ...s.td, padding: '9px 20px' }}>{col.state || '—'}</td>
                                    <td style={{ ...s.td, padding: '9px 20px' }}>{col.discount_offered || 'No'}</td>
                                    <td style={{ ...s.td, padding: '9px 20px', borderLeft: `2px solid ${c.border}` }}>{col.re_tax_bill_season || '—'}</td>
                                    <td style={{ ...s.td, padding: '9px 20px' }}>{col.re_fiscal_year || '—'}</td>
                                    <td style={{ ...s.td, padding: '9px 20px', textAlign: 'center' }}>{formatRatio(col.re_ratio)}</td>
                                    <td style={{ ...s.td, padding: '9px 20px', textAlign: 'center' }}>{instCount(col.entity_id, 'RE')}</td>
                                    <td style={{ ...s.td, padding: '9px 20px' }}>{adjSummary(col.entity_id, 'RE')}</td>
                                    {col.pp_exempt
                                      ? <td colSpan={5} style={{ ...s.td, padding: '9px 20px', textAlign: 'center', fontWeight: '700', color: c.text, borderLeft: `2px solid ${c.border}` }}>Exempt</td>
                                      : <>
                                        <td style={{ ...s.td, padding: '9px 20px', borderLeft: `2px solid ${c.border}` }}>{col.bpp_tax_bill_season || '—'}</td>
                                        <td style={{ ...s.td, padding: '9px 20px' }}>{col.bpp_fiscal_year || '—'}</td>
                                        <td style={{ ...s.td, padding: '9px 20px', textAlign: 'center' }}>{formatRatio(col.bpp_ratio)}</td>
                                        <td style={{ ...s.td, padding: '9px 20px', textAlign: 'center' }}>{instCount(col.entity_id, 'PP')}</td>
                                        <td style={{ ...s.td, padding: '9px 20px' }}>{adjSummary(col.entity_id, 'PP')}</td>
                                      </>}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                  );
                })}
            </tbody>
          </table>
        </ScrollX>
      </div>
      {addAssessorOpen && <AddAssessorModal isDark={isDark} assessor={null} defaultState={selectedState} valueYear={valueYear} onClose={() => setAddAssessorOpen(false)} onSuccess={onAssessorSaved} />}
    </div>
  );
}
